/*
 * Copyright 2025 coze-dev Authors
 * Modifications Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/* eslint-disable @typescript-eslint/naming-convention, @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/rules-of-hooks */

import { lazy, useEffect } from 'react';

import { resolveSameOriginRedirectURL } from './redirect-url';

// login page
export const LoginPage = lazy(() =>
  import('@adp-foundation/account-ui-adapter').then(res => ({
    default: res.LoginPage,
  })),
);

// documentation page
export const Redirect = lazy(() => import('../pages/redirect'));

// Workspace Sidebar Component
export const spaceSubMenu = lazy(() =>
  import('@adp-foundation/space-ui-adapter').then(exps => ({
    default: exps.WorkspaceSubMenu,
  })),
);

// Workspace Layout Component with end_user redirect guard
// Exception: end_user can access bot preview mode (?mode=preview)
export const SpaceLayout = lazy(() =>
  Promise.all([
    import('@adp-foundation/space-ui-adapter'),
    import('@adp-studio/user-store'),
    import('react-router-dom'),
  ]).then(([spaceAdapter, userStore, { Navigate, useSearchParams }]) => ({
    default: (props: any) => {
      const userInfo = userStore.userStoreService.useUserInfo();
      const isEndUser = userInfo?.user_type === 'end_user';
      const [searchParams] = useSearchParams();
      const isPreviewMode = searchParams.get('mode') === 'preview';
      const redirectURL = resolveSameOriginRedirectURL(
        searchParams.get('redirecturl'),
        window.location.href,
      );

      useEffect(() => {
        if (redirectURL) {
          window.location.replace(redirectURL);
        }
      }, [redirectURL]);

      if (redirectURL) {
        return null;
      }

      // Allow end_user to access preview mode for published agents
      if (isEndUser && !isPreviewMode) {
        return <Navigate to="/explore" replace />;
      }

      const OriginalSpaceLayout = spaceAdapter.SpaceLayout;
      return <OriginalSpaceLayout {...props} />;
    },
  })),
);

// A specific workspace layout component
export const SpaceIdLayout = lazy(() =>
  import('@adp-foundation/space-ui-base').then(exps => ({
    default: exps.SpaceIdLayout,
  })),
);

// project development page
export const Develop = lazy(() => import('../pages/develop'));

// resource library page
export const Library = lazy(() => import('../pages/library'));

// Agent IDE Layout Component
export const AgentIDELayout = lazy(
  () => import('@adp-agent-ide/layout-adapter'),
);

// Agent IDE page
export const AgentIDE = lazy(() =>
  import('@adp-agent-ide/entry-adapter').then(res => ({
    default: res.BotEditor,
  })),
);

// Agent IDE Release Page
export const AgentPublishPage = lazy(() =>
  import('@adp-agent-ide/agent-publish').then(exps => ({
    default: exps.AgentPublishPage,
  })),
);

// Project IDE Page
export const ProjectIDE = lazy(() =>
  import('@adp-project-ide/main').then(exps => ({
    default: exps.IDELayout,
  })),
);

// Project IDE Release Page
export const ProjectIDEPublish = lazy(() =>
  import('@adp-studio/project-publish').then(exps => ({
    default: exps.ProjectPublish,
  })),
);

// Knowledge Base Preview Page
export const KnowledgePreview = lazy(() =>
  import('@adp-studio/workspace-base/knowledge-preview').then(exps => ({
    default: exps.KnowledgePreviewPage,
  })),
);

// Knowledge base upload page
export const KnowledgeUpload = lazy(() =>
  import('@adp-studio/workspace-base/knowledge-upload').then(exps => ({
    default: exps.KnowledgeUploadPage,
  })),
);

// database resource page
export const DatabaseDetail = lazy(() =>
  import('@adp-studio/workspace-base').then(exps => ({
    default: exps.DatabaseDetailPage,
  })),
);

// workflow page
export const WorkflowPage = lazy(() =>
  import('@adp-workflow/playground-adapter').then(res => ({
    default: res.WorkflowPage,
  })),
);

// search page
export const SearchPage = lazy(() =>
  import('@adp-community/explore').then(exps => ({
    default: exps.SearchPage,
  })),
);

// plugin resource page layout component
export const PluginLayout = lazy(() => import('../pages/plugin/layout'));

// plugin resource page
export const PluginPage = lazy(() => import('../pages/plugin/page'));

// plugin tool page
export const PluginToolPage = lazy(() => import('../pages/plugin/tool/page'));

// Explore the experience page secondary navigation component
export const exploreSubMenu = lazy(() =>
  import('@adp-community/explore').then(exps => ({
    default: exps.ExploreSubMenu,
  })),
);

// template page
export const ExploreTemplatePage = lazy(() =>
  import('@adp-community/explore').then(exps => ({
    default: exps.TemplatePage,
  })),
);

// plugin store page
export const ExplorePluginPage = lazy(() =>
  import('@adp-community/explore').then(exps => ({
    default: exps.PluginPage,
  })),
);

// skill store page
export const ExploreSkillPage = lazy(() =>
  import('@adp-community/explore').then(exps => ({
    default: exps.SkillStorePage,
  })),
);

// Store page - published agents from all accessible spaces
export const ExploreStorePage = lazy(() =>
  import('@adp-community/explore').then(exps => ({
    default: exps.StorePage,
  })),
);

// Store agent detail page - chat with published agent
export const StoreAgentPage = lazy(() =>
  import('@adp-community/explore').then(exps => ({
    default: exps.StoreAgentPage,
  })),
);

// Team Management Page
export const TeamManagementPage = lazy(() =>
  import('@adp-studio/workspace-team-management').then(exps => ({
    default: exps.TeamManagementPage,
  })),
);

// Chat History Page
export const ChatHistoryPage = lazy(() => import('../pages/chat-history'));

// Log Management Page
export const LogManagementPage = lazy(() => import('../pages/log-management'));
// Token Usage Page
export const TokenUsagePage = lazy(() => import('../pages/token-usage'));

// Document Center Page
export const DocumentCenterPage = lazy(() => import('../pages/docs/index'));
